import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { publicSupabase } from "@/integrations/supabase/publicClient";
import { formatINR } from "@/lib/currency";
import { format } from "date-fns";
import { Check, Clock, Users, Package, Calendar, AlertCircle, CreditCard, RotateCcw, XCircle, Truck, MapPin, ExternalLink, PackageCheck, Gift, MessageSquare, Heart, Star, Instagram, Twitter, Camera, Share2, Sparkles, Upload, Image, ArrowLeft, Bell, Send, Facebook, Copy, Mail, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { CustomerHeader } from "@/components/CustomerHeader";
import { CustomerFooter } from "@/components/CustomerFooter";
import { useCelebration } from "@/components/CelebrationAnimation";
import { GiftPhotoGallery } from "@/components/GiftPhotoGallery";
import { ImportantDatesManager } from "@/components/ImportantDatesManager";
import { CustomerImportantDates } from "@/components/CustomerImportantDates";
import { RecurringReminderDialog } from "@/components/RecurringReminderDialog";
import { buildPublicAppUrl } from "@/lib/appConfig";
import { isRunningInsideShopifyAdmin, openShopifyTopLevel } from "@/lib/shopifyEmbedded";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabaseFunctionErrors";
import { isValidPanNumber, normalizePanNumber } from "@/lib/pan";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function SplitPaymentStatus() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { celebrate } = useCelebration();
  const hasCelebrated = useRef(false);

  const [searchParams] = useSearchParams();
  const participantEmail = searchParams.get("email");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [showGiftInput, setShowGiftInput] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [resendingParticipantId, setResendingParticipantId] = useState<string | null>(null);
  const [participantPan, setParticipantPan] = useState("");

  // Check for logged in user
  useEffect(() => {
    if (requestId && isRunningInsideShopifyAdmin()) {
      const search = window.location.search || "";
      openShopifyTopLevel(buildPublicAppUrl(`/split/${requestId}${search}`));
      return;
    }

    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    checkUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setCurrentUser(session?.user || null);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const { data: request, isLoading: requestLoading } = useQuery({
    queryKey: ["split-request", requestId],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("split_payment_requests")
        .select("*")
        .eq("id", requestId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!requestId,
  });

  // Fetch merchant info separately using the public RPC function
  const { data: merchantInfo } = useQuery({
    queryKey: ["merchant-info", request?.merchant_id],
    queryFn: async () => {
      if (!request?.merchant_id) return null;
      const { data, error } = await publicSupabase
        .rpc('get_split_merchant_info', { request_merchant_id: request.merchant_id });
      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!request?.merchant_id,
  });

  const { data: participants, isLoading: participantsLoading } = useQuery({
    queryKey: ["split-participants", requestId],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("split_payment_participants")
        .select("*")
        .eq("request_id", requestId);
      if (error) throw error;
      return data;
    },
    enabled: !!requestId,
  });

  // Fetch vouchers to get refund status
  const { data: vouchers } = useQuery({
    queryKey: ["split-vouchers", requestId],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("gift_vouchers")
        .select("*")
        .eq("request_id", requestId);
      if (error) throw error;
      return data;
    },
    enabled: !!requestId,
  });

  // Fetch gift photos
  const { data: giftPhotos, refetch: refetchPhotos } = useQuery({
    queryKey: ["gift-photos", requestId],
    queryFn: async () => {
      const { data, error } = await publicSupabase
        .from("gift_photos")
        .select("*")
        .eq("request_id", requestId)
        .order("uploaded_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!requestId,
  });

  // Helper to get voucher status for a participant
  const getParticipantVoucher = (participantId: string) => {
    return vouchers?.find(v => v.participant_id === participantId);
  };

  // Calculate these values early for the useEffect (safe even if request is null)
  const organizerParticipant = participants?.find((p) => p.is_organizer) || null;
  const organizerHasPaid = organizerParticipant?.has_paid || false;
  const paidCount = participants?.filter((p) => p.has_paid).length || 0;
  const totalParticipants = participants?.length || 0;
  const getParticipantContribution = (participant: typeof organizerParticipant) =>
    Number(participant?.contribution_amount ?? request?.split_amount ?? 0);
  const organizerContribution = getParticipantContribution(organizerParticipant);
  const totalCollected = participants?.reduce(
    (sum, participant) => participant.has_paid ? sum + getParticipantContribution(participant) : sum,
    0,
  ) || 0;
  const pendingAmount = participants?.reduce(
    (sum, participant) => !participant.has_paid ? sum + getParticipantContribution(participant) : sum,
    0,
  ) || 0;
  const pendingParticipantsCount = Math.max(totalParticipants - paidCount, 0);
  const allPaid = totalParticipants > 0 && paidCount === totalParticipants;
  const splitButtonColor = (merchantInfo as any)?.split_button_color || (merchantInfo as any)?.brand_color || "#3F51B5";
  const splitCardColor = (merchantInfo as any)?.split_card_color || "#F9FAFB";
  const currentParticipant = participantEmail
    ? participants?.find(p => p.email.toLowerCase() === participantEmail.toLowerCase())
    : null;
  const currentParticipantPan = normalizePanNumber(currentParticipant?.pan_number);
  const isPanCollectionEnabled = (merchantInfo as any)?.customer_pan_collection_enabled !== false;
  const isPanRequired = isPanCollectionEnabled && Boolean((merchantInfo as any)?.customer_pan_required);

  // Celebration effects - must be before early returns
  useEffect(() => {
    if (!request || hasCelebrated.current) return;
    
    if (request.fulfillment_status === 'delivered' && request.delivered_at) {
      hasCelebrated.current = true;
      celebrate('delivered');
    } else if (allPaid && request.status === 'completed') {
      hasCelebrated.current = true;
      celebrate('gift_on_way');
    } else if (allPaid && request.status !== 'completed') {
      hasCelebrated.current = true;
      celebrate('all_paid');
    }
  }, [request, allPaid, celebrate]);

  // Keep hook order stable for loading/not-found states reached from email links.
  useEffect(() => {
    setParticipantPan(currentParticipantPan);
  }, [currentParticipantPan]);

  if (requestLoading || participantsLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customer"></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-6">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Payment Not Found</h1>
            <p className="text-muted-foreground">
              This split payment link is invalid or has expired.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progressPercent = request?.total_price
    ? Math.min(100, (totalCollected / Number(request.total_price)) * 100)
    : 0;
  const isExpired = new Date(request.deadline) < new Date();
  const isRequestCancelled = request.status === 'expired' || request.status === 'cancelled';
  
  const effectiveParticipantPan = normalizePanNumber(participantPan || currentParticipantPan);
  const payAmount = Number(currentParticipant?.contribution_amount ?? request?.split_amount ?? 0);
  const hasCurrentUserPaid = currentParticipant?.has_paid || false;
  const currentUserVoucher = currentParticipant ? getParticipantVoucher(currentParticipant.id) : null;
  // Organizer tracking emails intentionally use the clean order URL. Keep
  // participant-specific email links scoped to that participant, while the
  // clean tracking URL exposes the organizer's sharing controls.
  const isOrganizerView = Boolean(currentParticipant?.is_organizer || !participantEmail);
  const pendingFriends = participants?.filter((participant) =>
    !participant.is_organizer && !participant.has_paid
  ) || [];
  const buildParticipantPaymentLink = (participantEmailToShare: string) =>
    buildPublicAppUrl(`/split/${request.id}?email=${encodeURIComponent(participantEmailToShare)}`);

  const handleCopyParticipantPaymentLink = async (participantEmailToShare: string) => {
    try {
      await navigator.clipboard.writeText(buildParticipantPaymentLink(participantEmailToShare));
      toast.success("Order placement link copied");
    } catch (error) {
      console.error("Error copying participant payment link:", error);
      toast.error("Could not copy order placement link");
    }
  };

  const handleShareParticipantPaymentLink = async (participant: { name?: string | null; email: string }) => {
    const paymentLink = buildParticipantPaymentLink(participant.email);
    const shareText = `${participant.name || "Your friend"} can complete their Splitkart order payment here: ${paymentLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: "Splitkart order payment link",
          text: shareText,
          url: paymentLink,
        });
      } else {
        await navigator.clipboard.writeText(paymentLink);
        toast.success("Order placement link copied");
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error sharing participant payment link:", error);
        toast.error("Could not share order placement link");
      }
    }
  };

  const handleResendParticipantPaymentLink = async (participantId: string) => {
    setResendingParticipantId(participantId);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-participant-checkouts", {
        body: {
          requestId: request.id,
          participantId,
          force: true,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      toast.success(data?.sent ? "Order placement link emailed" : data?.message || "No pending participant email was sent");
    } catch (error: any) {
      console.error("Error resending participant payment email:", error);
      const errorMessage = await getSupabaseFunctionErrorMessage(error, "Failed to send order placement link");
      toast.error(errorMessage);
    } finally {
      setResendingParticipantId(null);
    }
  };

  const handlePayNow = async () => {
    if (!currentParticipant || isExpired) return;

    if (isPanCollectionEnabled && effectiveParticipantPan && !isValidPanNumber(effectiveParticipantPan)) {
      toast.error("Enter a valid PAN in the format ABCDE1234F.");
      return;
    }

    if (isPanRequired && !effectiveParticipantPan) {
      toast.error("PAN is required before payment for this store.");
      return;
    }
    
    const paymentProvider = merchantInfo?.splitkart_payment_provider || 'razorpay';
    
    setIsProcessingPayment(true);
    try {
      if (isPanCollectionEnabled && effectiveParticipantPan && effectiveParticipantPan !== currentParticipantPan) {
        const { data, error: updatePanError } = await supabase.functions.invoke("update-participant-pan", {
          body: {
            requestId: request.id,
            participantId: currentParticipant.id,
            participantEmail: currentParticipant.email,
            panNumber: effectiveParticipantPan,
          },
        });

        if (updatePanError) {
          throw updatePanError;
        }

        if (data?.error) {
          throw new Error(data.error);
        }
      }

      if (paymentProvider === 'shopify' || merchantInfo?.platform_type === 'woocommerce') {
        // Shopify and WooCommerce both collect participant shares through the
        // merchant's native checkout so the store's configured gateway is used.
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-participant-checkout`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              requestId: request.id,
              participantId: currentParticipant.id,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to create checkout");
        }

        const data = await response.json();
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          throw new Error("No checkout URL returned");
        }
      } else if (paymentProvider === 'stripe') {
        // Stripe payment flow
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-stripe-payment-intent`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount: payAmount,
              currency: merchantInfo?.currency || 'USD',
              participantId: currentParticipant.id,
              customerEmail: currentParticipant.email,
              customerName: currentParticipant.name,
              purchaseId: request.id,
            }),
          }
        );

        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || "Failed to create Stripe payment");
        }
        
        toast.info("Stripe checkout will open once configured by the organizer.");
        setIsProcessingPayment(false);
      } else {
        // Razorpay (default)
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-razorpay-order`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount: payAmount,
              participantId: currentParticipant.id,
              productName: request.product_name,
              customerEmail: currentParticipant.email,
              customerName: currentParticipant.name,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to create payment order");
        }

        const orderData = await response.json();

        // Load Razorpay script if not already loaded
        if (!(window as any).Razorpay) {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load Razorpay"));
            document.body.appendChild(script);
          });
        }

        // Open Razorpay checkout
        const options = {
          key: orderData.keyId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: "Shakatah",
          description: `Split payment for ${request.product_name}`,
          image: request.product_image_url || undefined,
          order_id: orderData.orderId,
          handler: async (response: any) => {
            const verifyResponse = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-razorpay-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  participantId: currentParticipant.id,
                  giftMessage: giftMessage.trim() ? giftMessage.trim() : null,
                }),
              }
            );

            const verifyData = await verifyResponse.json();
            
            if (verifyData.verified) {
              toast.success("Payment successful!");
              window.location.reload();
            } else {
              toast.error("Payment verification failed. Please contact support.");
            }
          },
          prefill: {
            name: currentParticipant.name,
            email: currentParticipant.email,
            contact: currentParticipant.mobile,
          },
          theme: {
            color: splitButtonColor,
          },
          modal: {
            ondismiss: () => {
              setIsProcessingPayment(false);
              toast.info("Payment was not completed. Your payment link is still active until the deadline.");
            },
          },
        };

        const razorpay = new (window as any).Razorpay(options);
        razorpay.on("payment.failed", (response: any) => {
          console.error("Razorpay payment failed:", response?.error);
          toast.error("Payment failed. Your link is still active, so you can try again.");
          setIsProcessingPayment(false);
        });
        razorpay.open();
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CustomerHeader showMyPurchasesLink={false} />
      
      <div className="py-6 px-4">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Back navigation */}
          <Link 
            to="/my-purchases"
            className="inline-flex items-center text-sm text-customer hover:text-customer/80 transition-colors -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            My Dashboard
          </Link>

          {/* Organizer funding banner */}
          {request.status === 'pending' && participants && participants.length > 0 && !isRequestCancelled && organizerHasPaid && (
            <Card className="border-green-200 dark:border-green-800 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/50">
                    <Send className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-200">
                      Organizer payment confirmed
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {pendingParticipantsCount > 0
                        ? `${formatINR(pendingAmount)} is still pending. ${paidCount} of ${totalParticipants} participants have paid so far, including the organizer.`
                        : 'Your friends have been notified and can now pay their share.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recurring Reminder Prompt - Show for organizer on gift orders after payment confirmation */}
          {request.is_gift && request.status === 'pending' && participants && participants.length > 0 && !isRequestCancelled && (
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      Is this a recurring occasion?
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Birthdays, anniversaries, and other special dates come around every year. Set a reminder so you're ready next time!
                    </p>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReminderDialog(true)}
                      className="gap-2"
                    >
                      <Bell className="h-4 w-4" />
                      Set a Reminder for Next Year
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Recurring Reminder Dialog */}
          <RecurringReminderDialog
            open={showReminderDialog}
            onOpenChange={setShowReminderDialog}
            requestId={requestId}
            recipientName={request.gift_recipient_name || request.product_name}
            productName={request.product_name}
          />

        {/* Product Preview */}
        <Card className="overflow-hidden" style={{ backgroundColor: splitCardColor }}>
          {request.product_image_url && !imageError && (
            <div className="aspect-video w-full bg-muted relative">
              <img
                src={request.product_image_url}
                alt={request.product_name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (img.naturalWidth === 0) {
                    setImageError(true);
                  }
                }}
              />
            </div>
          )}
          <CardHeader className="pb-3">
            {/* Store name - show if available */}
            {merchantInfo?.store_name && (
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                From {merchantInfo.store_name}
              </p>
            )}
            {/* Product name - always show */}
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="h-5 w-5 flex-shrink-0 text-primary" />
              <span className="break-words">{request.product_name || "Group Purchase"}</span>
            </CardTitle>
            {/* Product description - show if available */}
            {request.product_description && (
              <p className="text-muted-foreground text-sm leading-relaxed mt-2">
                {request.product_description}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-muted-foreground">Total Price</span>
              <span className="font-semibold text-lg">
                {formatINR(Number(request.total_price))}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">
                {request.split_type === "custom" ? "Organizer Pays" : "Each Person Pays"}
              </span>
              <span className="font-semibold text-primary text-lg">
                {formatINR(organizerContribution)}
              </span>
            </div>
            {organizerHasPaid && (
              <>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Collected so far</span>
                  <span className="font-semibold text-green-600">
                    {formatINR(totalCollected)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Still pending</span>
                  <span className="font-semibold text-amber-600">
                    {formatINR(pendingAmount)}
                  </span>
                </div>
              </>
            )}
            
            {/* Pay Now Button for participants */}
            {currentParticipant && !hasCurrentUserPaid && !isExpired && !isRequestCancelled && (
              <div className="pt-4 border-t space-y-4">
                {isPanCollectionEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="participant-pan" className="text-sm font-medium">
                      PAN Number {isPanRequired ? "*" : "(optional)"}
                    </Label>
                    {isPanRequired && (
                      <Badge variant="secondary" className="text-[10px]">Required by merchant</Badge>
                    )}
                  </div>
                  <Input
                    id="participant-pan"
                    placeholder="ABCDE1234F"
                    value={participantPan}
                    onChange={(e) => setParticipantPan(normalizePanNumber(e.target.value))}
                    maxLength={10}
                  />
                  {participantPan && !isValidPanNumber(participantPan) && (
                    <p className="text-xs text-destructive">Enter a valid PAN in the format ABCDE1234F.</p>
                  )}
                </div>
                )}

                {/* Personal Message Input - Show for all split payments */}
                <div className="space-y-2">
                  <div 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setShowGiftInput(!showGiftInput)}
                  >
                    <MessageSquare className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">
                      {showGiftInput ? 'Hide message' : `Add a personal note${request.is_gift ? ' for the recipient' : ''} (optional)`}
                    </span>
                  </div>
                  {showGiftInput && (
                    <div className="space-y-2">
                      <Textarea
                        placeholder={request.is_gift 
                          ? "Write a personal message for the recipient..." 
                          : "Add a note for the group, e.g., 'Happy to chip in!'"}
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        className="min-h-[80px] resize-none"
                        maxLength={250}
                      />
                      <p className="text-xs text-muted-foreground text-right">
                        {giftMessage.length}/250
                      </p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={handlePayNow}
                  disabled={isProcessingPayment}
                  className="w-full text-white hover:opacity-90"
                  size="lg"
                  style={{ backgroundColor: splitButtonColor }}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {isProcessingPayment ? "Processing..." : `Pay ${formatINR(payAmount)} Now${merchantInfo?.splitkart_payment_provider === 'shopify' || merchantInfo?.platform_type === 'woocommerce' ? ' via Checkout' : ''}`}
                </Button>
              </div>
            )}
            
            {currentParticipant && hasCurrentUserPaid && !currentUserVoucher?.status?.includes('refund') && (
              <div className="pt-4 border-t">
                <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                  <Check className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="font-medium text-green-800 dark:text-green-200">
                    You've already paid your share!
                  </p>
                </div>
              </div>
            )}
            
            {currentParticipant && currentUserVoucher?.status === 'pending_refund' && (
              <div className="pt-4 border-t">
                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-center">
                  <RotateCcw className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Refund Processing
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Your payment of {formatINR(payAmount)} is being refunded. You'll receive the refund within 5-7 business days.
                  </p>
                </div>
              </div>
            )}
            
            {currentParticipant && currentUserVoucher?.status === 'refunded' && (
              <div className="pt-4 border-t">
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
                  <Check className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="font-medium text-blue-800 dark:text-blue-200">
                    Refund Completed
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Your payment of {formatINR(payAmount)} has been refunded successfully.
                  </p>
                </div>
              </div>
            )}
            
            {currentParticipant && isExpired && !hasCurrentUserPaid && !isRequestCancelled && (
              <div className="pt-4 border-t">
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                  <AlertCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                  <p className="font-medium text-red-800 dark:text-red-200">
                    Payment deadline has passed
                  </p>
                </div>
              </div>
            )}
            
            {isRequestCancelled && !hasCurrentUserPaid && (
              <div className="pt-4 border-t">
                <div className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-4 text-center">
                  <XCircle className="h-6 w-6 text-gray-600 mx-auto mb-2" />
                  <p className="font-medium text-gray-800 dark:text-gray-200">
                    Order Cancelled
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    This group purchase was cancelled as not all participants completed payment by the deadline.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gift Messages Section - Only show for gifts with messages to recipient */}
        {request.is_gift && (request.organizer_gift_message || participants?.some(p => p.gift_message)) && (
          <Card className="border-pink-200 dark:border-pink-900 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-pink-700 dark:text-pink-300">
                <Heart className="h-5 w-5 fill-pink-500 text-pink-500" />
                Messages for the Recipient
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Organizer's gift message */}
              {request.organizer_gift_message && (
                <div className="p-4 bg-white/80 dark:bg-gray-900/50 rounded-xl border border-pink-100 dark:border-pink-900">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {request.customer_name?.charAt(0)?.toUpperCase() || 'O'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{request.customer_name}</p>
                      <p className="text-xs text-muted-foreground">Organizer</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80 italic">"{request.organizer_gift_message}"</p>
                </div>
              )}
              
              {/* Contributors' messages for the recipient */}
              {participants?.filter(p => p.gift_message).map((participant) => (
                <div key={participant.id} className="p-4 bg-white/80 dark:bg-gray-900/50 rounded-xl border border-purple-100 dark:border-purple-900">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-purple-100 dark:bg-purple-900">
                      <span className="text-sm font-semibold text-purple-600 dark:text-purple-300">
                        {participant.name?.charAt(0)?.toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{participant.name}</p>
                      <p className="text-xs text-muted-foreground">Contributor</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80 italic">"{participant.gift_message}"</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Deadline */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span className="text-muted-foreground">Deadline</span>
              </div>
              <div className="text-right">
                <p className="font-medium">
                  {format(new Date(request.deadline), "PPp")}
                </p>
                {isExpired ? (
                  <Badge variant="destructive" className="mt-1">Expired</Badge>
                ) : (
                  <Badge variant="secondary" className="mt-1">Active</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Tracking - Show when order is completed */}
        {request.status === 'completed' && (
          <Card className="border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Truck className="h-5 w-5" />
                Order Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Fulfillment Status */}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge 
                  variant={
                    request.fulfillment_status === 'delivered' ? 'default' :
                    request.fulfillment_status === 'shipped' || request.fulfillment_status === 'out_for_delivery' ? 'secondary' :
                    request.fulfillment_status === 'failed' ? 'destructive' : 'outline'
                  }
                  className={request.fulfillment_status === 'delivered' ? 'bg-green-600' : ''}
                >
                  {request.fulfillment_status === 'delivered' && <PackageCheck className="h-3 w-3 mr-1" />}
                  {request.fulfillment_status === 'shipped' && <Truck className="h-3 w-3 mr-1" />}
                  {request.fulfillment_status === 'out_for_delivery' && <MapPin className="h-3 w-3 mr-1" />}
                  {request.fulfillment_status === 'pending' ? 'Processing' : 
                   request.fulfillment_status === 'shipped' ? 'Shipped' :
                   request.fulfillment_status === 'out_for_delivery' ? 'Out for Delivery' :
                   request.fulfillment_status === 'delivered' ? 'Delivered' :
                   request.fulfillment_status === 'failed' ? 'Delivery Failed' : 'Processing'}
                </Badge>
              </div>

              {/* Tracking Progress */}
              <div className="relative pt-2">
                <div className="flex justify-between mb-2">
                  {['Processing', 'Shipped', 'Out for Delivery', 'Delivered'].map((step, idx) => {
                    const statusOrder = ['pending', 'shipped', 'out_for_delivery', 'delivered'];
                    const currentIdx = statusOrder.indexOf(request.fulfillment_status || 'pending');
                    const isComplete = idx <= currentIdx;
                    const isCurrent = idx === currentIdx;
                    
                    return (
                      <div key={step} className="flex flex-col items-center flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          isComplete ? 'bg-primary text-primary-foreground' : 
                          'bg-muted text-muted-foreground'
                        } ${isCurrent ? 'ring-2 ring-primary ring-offset-2' : ''}`}>
                          {isComplete ? <Check className="h-4 w-4" /> : idx + 1}
                        </div>
                        <span className={`text-xs mt-1 text-center ${isComplete ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="absolute top-6 left-4 right-4 h-0.5 bg-muted -z-10">
                  <div 
                    className="h-full bg-primary transition-all duration-500" 
                    style={{ 
                      width: `${
                        request.fulfillment_status === 'pending' ? '0%' :
                        request.fulfillment_status === 'shipped' ? '33%' :
                        request.fulfillment_status === 'out_for_delivery' ? '66%' :
                        request.fulfillment_status === 'delivered' ? '100%' : '0%'
                      }` 
                    }}
                  />
                </div>
              </div>

              {/* Tracking Details */}
              {request.tracking_number && (
                <div className="pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Carrier</span>
                    <span className="font-medium">{request.carrier || 'Shipping Partner'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Tracking Number</span>
                    <span className="font-mono text-sm">{request.tracking_number}</span>
                  </div>
                  {request.tracking_url && (
                    <Button asChild variant="outline" className="w-full">
                      <a href={request.tracking_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Track on Carrier Website
                      </a>
                    </Button>
                  )}
                </div>
              )}

              {/* Delivery Confirmation with Social Sharing */}
              {request.fulfillment_status === 'delivered' && request.delivered_at && (
                <div className="pt-4 border-t space-y-4">
                  <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                    <PackageCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="font-medium text-green-800 dark:text-green-200">
                      Order Delivered! 🎉
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      Delivered on {format(new Date(request.delivered_at), "PPp")}
                    </p>
                  </div>

                  {/* Fun Social Sharing Section */}
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border border-pink-200 dark:border-pink-800 rounded-xl p-5 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300 text-sm font-medium mb-3">
                      <Sparkles className="w-4 h-4" />
                      Share the Joy!
                    </div>
                    
                    <h4 className="font-semibold text-foreground mb-2">
                      Got a pic of your friend with the gift?
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Tag us on social and make memories last forever! 📸
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700 dark:bg-green-950 dark:border-green-700 dark:text-green-400"
                        onClick={() => {
                          const text = `Just received an amazing group gift! Thanks to everyone who chipped in! 🎁💜 Made easy with Shakatah! https://www.shakatah.com`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        WhatsApp
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-pink-300 text-pink-600 hover:bg-pink-50 dark:border-pink-700 dark:text-pink-400 dark:hover:bg-pink-950"
                        onClick={() => {
                          const text = `Just received an amazing group gift! Thanks to everyone who chipped in! 🎁💜 #Shakatah #GroupGifting`;
                          window.open(`https://www.instagram.com/`, '_blank');
                          navigator.clipboard.writeText(text);
                          toast.success("Caption copied! Share your photo on Instagram");
                        }}
                      >
                        <Instagram className="h-4 w-4 mr-1" />
                        Instagram
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-300 text-blue-500 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950"
                        onClick={() => {
                          const text = `Just received an amazing group gift from my friends! 🎁 Thanks to @shakatah for making group gifting so easy! #Shakatah`;
                          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                      >
                        <Twitter className="h-4 w-4 mr-1" />
                        Twitter
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="bg-blue-50 hover:bg-blue-100 border-blue-400 text-blue-600 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-400"
                        onClick={() => {
                          const url = `https://www.shakatah.com`;
                          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                        }}
                      >
                        <Facebook className="h-4 w-4 mr-1" />
                        Facebook
                      </Button>
                    </div>
                  </div>
                  
                  {/* Gift Photo Gallery */}
                  <GiftPhotoGallery
                    requestId={requestId!}
                    photos={giftPhotos || []}
                    onPhotoUploaded={() => refetchPhotos()}
                    participantName={currentParticipant?.name}
                  />
                </div>
              )}

              {/* Awaiting Fulfillment */}
              {request.fulfillment_status === 'pending' && !request.tracking_number && (
                <div className="pt-4 border-t">
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
                    <Package className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="font-medium text-blue-800 dark:text-blue-200">
                      Order Being Prepared
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                      The merchant is preparing your order. Shipping and tracking updates will come from the store's own order emails once the shipment is created.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Payment Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {formatINR(totalCollected)} collected of {formatINR(Number(request.total_price))}
                </span>
                <span className="font-medium">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-muted-foreground mb-1">Participants paid</p>
                <p className="font-semibold">{paidCount} of {totalParticipants}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-muted-foreground mb-1">Amount pending</p>
                <p className="font-semibold text-amber-600">{formatINR(pendingAmount)}</p>
              </div>
            </div>

            {allPaid && (
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                <Check className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-green-800 dark:text-green-200">
                  All payments received!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Participants */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Participants</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Customer (initiator/organizer) */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">
                    {request.customer_name?.charAt(0)?.toUpperCase() || 'O'}
                  </span>
              </div>
              <div>
                <p className="font-medium">{request.customer_name}</p>
                <p className="text-xs text-muted-foreground">
                  Organizer · {formatINR(organizerContribution)}
                </p>
              </div>
            </div>
              {organizerHasPaid ? (
                <Badge className="bg-green-600 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Paid
                </Badge>
              ) : (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Pending
                </Badge>
              )}
            </div>

            {/* Other participants - filter out organizer to avoid duplicate */}
            {participants?.filter(p => !p.is_organizer).map((participant) => {
              const voucher = getParticipantVoucher(participant.id);
              const refundStatus = voucher?.status;
              
              return (
                <div
                  key={participant.id}
                  className="p-3 bg-muted/50 rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${participant.has_paid ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                        <span className={`text-sm font-semibold ${participant.has_paid ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                          {participant.name?.charAt(0)?.toUpperCase() || 'P'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{participant.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatINR(getParticipantContribution(participant))}
                        </p>
                      </div>
                    </div>
                    {refundStatus === 'refunded' ? (
                      <Badge className="bg-blue-600 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Refunded
                      </Badge>
                    ) : refundStatus === 'pending_refund' ? (
                      <Badge className="bg-amber-600 flex items-center gap-1">
                        <RotateCcw className="h-3 w-3" />
                        Refund Processing
                      </Badge>
                    ) : participant.has_paid ? (
                      <Badge className="bg-green-600 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Paid
                      </Badge>
                    ) : isRequestCancelled ? (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <XCircle className="h-3 w-3" />
                        Not Paid
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Pending
                      </Badge>
                    )}
                  </div>
                  {/* Show participant's gift message if they have one */}
                  {request.is_gift && participant.gift_message && (
                    <div className="ml-12 pl-3 border-l-2 border-pink-200 dark:border-pink-800">
                      <p className="text-sm text-muted-foreground italic">"{participant.gift_message}"</p>
                    </div>
                  )}
                </div>
              );
            })}

            {isOrganizerView && pendingFriends.length > 0 && !isRequestCancelled && !isExpired && (
              <div className="mt-4 space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div>
                  <p className="font-semibold">Share payment links with friends</p>
                  <p className="text-sm text-muted-foreground">
                    Copy, share, or resend each friend&apos;s secure order-placement link.
                  </p>
                </div>

                {pendingFriends.map((participant) => (
                  <div
                    key={`share-${participant.id}`}
                    className="flex flex-col gap-3 rounded-lg border bg-background p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="font-medium">{participant.name || participant.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatINR(getParticipantContribution(participant))} pending
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyParticipantPaymentLink(participant.email)}
                      >
                        <Copy className="mr-1 h-3 w-3" />
                        Copy link
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleShareParticipantPaymentLink(participant)}
                      >
                        <Share2 className="mr-1 h-3 w-3" />
                        Share link
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleResendParticipantPaymentLink(participant.id)}
                        disabled={resendingParticipantId === participant.id}
                      >
                        {resendingParticipantId === participant.id ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Mail className="mr-1 h-3 w-3" />
                        )}
                        Send order link
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leave a Review - Show when delivered */}
        {request.status === 'completed' && request.fulfillment_status === 'delivered' && (
          <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <CardContent className="pt-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 mb-4">
                <Star className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Loved the gift?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Help others discover amazing group gifting! Leave a quick review.
              </p>
              <div className="flex justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button 
                    key={star} 
                    className="p-1 hover:scale-110 transition-transform"
                    onClick={() => toast.success(`Thanks for your ${star}-star rating! 💜`)}
                  >
                    <Star className="h-6 w-6 text-amber-400 fill-amber-400 hover:fill-amber-500" />
                  </button>
                ))}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400"
                onClick={() => toast.success("Thank you for your feedback! 🙏")}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Write a Review
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Important Dates Section - For logged in users */}
        {currentUser && (
          <ImportantDatesManager userId={currentUser.id} />
        )}

        {/* Reminder Prompt for Gift Orders - Non-logged in users */}
        {!currentUser && request.is_gift && request.status === 'completed' && (
          <>
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6 text-center space-y-3">
                <Bell className="h-8 w-8 text-primary mx-auto" />
                <h3 className="font-semibold">Never miss an occasion</h3>
                <p className="text-sm text-muted-foreground">
                  Set reminders for birthdays, anniversaries, and other important dates so you're always ready to organize the next group purchase.
                </p>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => setShowReminderDialog(true)}
                >
                  🔔 Set an Alert
                </Button>
              </CardContent>
            </Card>
            
            {/* Reminder Dialog - Only visible when opened */}
            {showReminderDialog && (
              <CustomerImportantDates
                customerEmail={participantEmail}
                compact={true}
                dialogOpenProp={showReminderDialog}
                onDialogClose={() => setShowReminderDialog(false)}
              />
            )}

            {/* Social Sharing Section */}
            <Card className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border border-pink-200 dark:border-pink-800">
              <CardContent className="pt-6 text-center space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-300 text-sm font-medium">
                  <Sparkles className="w-4 h-4" />
                  Share the Joy!
                </div>
                <p className="text-sm text-muted-foreground">
                  Tag us on social and inspire others to gift together! 📸
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700 dark:bg-green-950 dark:border-green-700 dark:text-green-400"
                    onClick={() => {
                      const text = `Check out Splitkart - makes group gifting so easy! 🎁💜 https://splitkart.shakatah.com`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-pink-300 text-pink-600 hover:bg-pink-50 dark:border-pink-700 dark:text-pink-400"
                    onClick={() => {
                      const text = `Check out Splitkart - makes group gifting so easy! 🎁💜 #Splitkart #GroupGifting`;
                      window.open(`https://www.instagram.com/`, '_blank');
                      navigator.clipboard.writeText(text);
                      toast.success("Caption copied! Share on Instagram");
                    }}
                  >
                    <Instagram className="h-4 w-4 mr-1" />
                    Instagram
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-blue-300 text-blue-500 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                    onClick={() => {
                      const text = `Discovered Splitkart - perfect for organizing group gifts! 🎁 #GroupGifting`;
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                  >
                    <Twitter className="h-4 w-4 mr-1" />
                    Twitter
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="bg-blue-50 hover:bg-blue-100 border-blue-400 text-blue-600 dark:bg-blue-950 dark:border-blue-700 dark:text-blue-400"
                    onClick={() => {
                      const url = `https://www.shakatah.com`;
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                    }}
                  >
                    <Facebook className="h-4 w-4 mr-1" />
                    Facebook
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Footer */}
        </div>
      </div>
      
      <CustomerFooter />
    </div>
  );
}

export default SplitPaymentStatus;
